<template>
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <CardDescription>Customise the colours of your cashback catalogue</CardDescription>
        <CardAction class="flex items-center gap-2">
          <Button size="sm" @click="updateColoursSDK">
            <Save class="w-4 h-4" />
            Save
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div v-if="loading" class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Skeleton v-for="i in colourFields.length" :key="`sk${i}`" class="h-11" />
        </div>
        <div v-else class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label
            v-for="field in colourFields"
            :key="field.key"
            class="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
          >
            <input
              type="color"
              class="size-9 shrink-0 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
              v-model="sdkColours[field.key]"
              :title="field.label"
            />
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-foreground truncate">{{ field.label }}</p>
              <p class="text-xs text-muted-foreground font-mono uppercase">{{ sdkColours[field.key] }}</p>
            </div>
          </label>
        </div>
      </CardContent>
    </Card>

    <div class="flex flex-col items-center gap-4">
      <div class="inline-flex items-center rounded-lg border border-border p-0.5 text-sm">
        <button
          v-for="d in devices"
          :key="d.id"
          type="button"
          class="px-4 py-1 rounded-md transition-colors"
          :class="device === d.id ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'"
          @click="device = d.id"
        >
          {{ d.label }}
        </button>
      </div>

      <div style="zoom: 75%">
        <div :class="['device-frame', device]">
          <div
            :key="componentKey"
            :style="`background-color:${sdkColours.backgroundColour}; color:${sdkColours.primaryTextColour}`"
            class="device-screen"
          >
            <div class="device-island"></div>
            <div class="device-punchhole"></div>
            <div id="offerSDKHolder" class="w-full"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction, Button, Skeleton } from '@boogi/ui'
import { Save } from 'lucide-vue-next'
import BoogiSDK from 'boogi-javascript-sdk'

export default {
  name: 'manage-sdk',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Skeleton,
    Save,
  },
  data() {
    return {
      loading: false,
      componentKey: 0,
      device: 'iphone',
      devices: [
        { id: 'iphone', label: 'iPhone' },
        { id: 'samsung', label: 'Samsung' },
      ],

      colourFields: [
        { key: 'backgroundColour', label: 'Background colour' },
        { key: 'cardBackgroundColour', label: 'Card background colour' },
        { key: 'cardBorderColour', label: 'Card border colour' },
        { key: 'primaryTextColour', label: 'Primary font colour' },
        { key: 'secondaryTextColour', label: 'Secondary font colour' },
        { key: 'primaryButtonBackground', label: 'Primary button colour' },
        { key: 'primaryButtonBorder', label: 'Primary button border colour' },
        { key: 'primaryButtonTextColour', label: 'Primary button text colour' },
        { key: 'secondaryButtonBackground', label: 'Secondary button colour' },
        { key: 'secondaryButtonBorder', label: 'Secondary button border colour' },
        { key: 'secondaryButtonTextColour', label: 'Secondary button text colour' },
        { key: 'dividerColour', label: 'Divider colour' },
        { key: 'highlightTextColour', label: 'Highlight text colour' },
        { key: 'sectionLabelTitleColour', label: 'Section label title colour' },
        { key: 'sectionLabelSubTitleColour', label: 'Section label subtitle colour' },
      ],

      sdkColours: {
        sectionLabelTitleColour: '#F6F6F6',
        sectionLabelSubTitleColour: '#B3B5B9',
        primaryTextColour: '#F6F6F6',
        secondaryTextColour: '#B3B5B9',
        highlightTextColour: '#73f46c',
        cardBackgroundColour: '#000814',
        cardBorderColour: '#000814',
        dividerColour: '#333943',
        primaryButtonBackground: '#fcbc2c',
        primaryButtonBorder: '#fcbc2c',
        primaryButtonTextColour: '#19212B',
        secondaryButtonBackground: '#323943',
        secondaryButtonBorder: '#323943',
        secondaryButtonTextColour: '#CAD5E6',
        backgroundColour: '#19212B',
      },
    }
  },
  created() {
    this.getColoursSDK()
  },
  methods: {
    async fetchIdFromAPI() {
      const token = localStorage.getItem('token')
      const api = localStorage.getItem('apiType')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
      try {
        const response = await fetch(`${apiUrl}/private/api/client/api-key/sdk/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch ID from API')
        }
        const data = await response.json()
        const boogiSDK = new BoogiSDK()
        boogiSDK.displayOffers(data.token, api, 'offerSDKHolder', { height: '100%' })
      } catch (error) {
        console.error('Error fetching ID from API:', error)
      }
    },

    async getColoursSDK() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true
        const response = await fetch(`${apiUrl}/private/api/client/sdk`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.sdkColours = object
          delete this.sdkColours.createdOn
          delete this.sdkColours.id
          delete this.sdkColours.updatedOn
          delete this.sdkColours.publisherId

          this.loading = false
          this.fetchIdFromAPI()
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    async updateColoursSDK() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        console.log(this.sdkColours)

        this.loading = true
        const response = await fetch(`${apiUrl}/private/api/client/sdk`, {
          method: 'POST',
          body: JSON.stringify(this.sdkColours),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          var object = await response.json()
          this.sdkColours = object
          delete this.sdkColours.createdOn
          delete this.sdkColours.id
          delete this.sdkColours.updatedOn
          delete this.sdkColours.publisherId
          this.loading = false
          this.componentKey += 1
          this.fetchIdFromAPI()
          // localStorage.setItem('colours',object)
          // this.getOfferById(this.individualRetailer.programOffers[0].id);
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
<style scoped>
/* Shared device shell (bezel) */
.device-frame {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(150deg, #3a3a3c 0%, #1c1c1e 55%, #000 100%);
  box-shadow:
    0 20px 45px -20px rgba(0, 0, 0, 0.55),
    inset 0 0 0 2px rgba(255, 255, 255, 0.06);
}

/* iPhone: thin uniform bezel, very round corners, dynamic island */
.device-frame.iphone {
  width: 392px;
  height: 812px;
  padding: 12px;
  border-radius: 56px;
}

/* Samsung: slightly narrower bezel, less rounded corners, punch-hole camera */
.device-frame.samsung {
  width: 384px;
  height: 820px;
  padding: 8px;
  border-radius: 38px;
  background: linear-gradient(150deg, #2b2f36 0%, #14171c 60%, #000 100%);
}

.device-screen {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

.device-frame.iphone .device-screen {
  border-radius: 44px;
}
.device-frame.samsung .device-screen {
  border-radius: 30px;
}

/* iPhone dynamic island — only shown on iPhone */
.device-island {
  display: none;
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  width: 112px;
  height: 30px;
  background: #000;
  border-radius: 16px;
  z-index: 10;
}
.device-frame.iphone .device-island {
  display: block;
}

/* Keep the SDK content clear of the island / punch-hole. The exposed strip is part of
   .device-screen, which already carries the configured background colour, so it reads as
   the app's own background rather than a gap. */
.device-frame.iphone #offerSDKHolder {
  padding-top: 56px;
}
.device-frame.samsung #offerSDKHolder {
  padding-top: 34px;
}

/* Samsung centered punch-hole camera — only shown on Samsung */
.device-punchhole {
  display: none;
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: 12px;
  height: 12px;
  background: #000;
  border-radius: 50%;
  z-index: 10;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
}
.device-frame.samsung .device-punchhole {
  display: block;
}

#offerSDKHolder {
  max-width: 500px;
  height: 100%;
  width: 100%;
  margin: 0 auto;
}
iframe {
  height: 100%;
}
</style>
